/**
 * <freq-dropdown>
 *
 * Delivery interval picker, used on the product page and on each subscription line
 * in the cart. Options are rendered by Liquid from the product's selling plan group,
 * so however many frequencies exist in the subscription app is what appears — no
 * list is hardcoded here.
 *
 * A button and listbox rather than a <select>, so it can be styled and can carry the
 * discount alongside each interval.
 *
 * On the product page it just reports the choice and the card handles it. On a cart
 * line (data-line-key present) it changes that line's selling plan and refreshes the
 * cart.
 */
if (!customElements.get('freq-dropdown')) {
  customElements.define(
    'freq-dropdown',
    class FreqDropdown extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('.fd__button');
        this.valueEl = this.querySelector('.fd__value');
        this.list = this.querySelector('.fd__list');
        this.options = Array.from(this.querySelectorAll('.fd__option'));
        this.lineKey = this.dataset.lineKey || '';

        if (!this.button || !this.list || !this.options.length) return;

        this.onButtonClick = this.onButtonClick.bind(this);
        this.onOptionClick = this.onOptionClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);

        this.button.addEventListener('click', this.onButtonClick);
        this.list.addEventListener('click', this.onOptionClick);
        this.addEventListener('keydown', this.onKeydown);
        document.addEventListener('click', this.onDocumentClick);

        this.syncFromSelected();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
      }

      /* ── Open state ────────────────────────────────────────────────────── */

      get open() {
        return this.button?.getAttribute('aria-expanded') === 'true';
      }

      setOpen(open) {
        this.button.setAttribute('aria-expanded', open ? 'true' : 'false');
        this.list.hidden = !open;
        this.classList.toggle('is-open', open);
        if (open) {
          this.revealAboveBottomBar();
          this.placeList();
          if (!this.onViewportChange) this.onViewportChange = () => this.open && this.placeList();
          window.addEventListener('scroll', this.onViewportChange, { passive: true });
          window.addEventListener('resize', this.onViewportChange);
        } else {
          this.classList.remove('fd--up');
          if (this.onViewportChange) {
            window.removeEventListener('scroll', this.onViewportChange);
            window.removeEventListener('resize', this.onViewportChange);
          }
        }
        if (open) this.currentOption()?.focus({ preventScroll: true });
      }

      /**
       * Open downwards when the list fits below the button, upwards when it does not and
       * there is more room above - near the bottom of the screen (or above the sticky buy
       * bar) a downward list was cut off after its first option.
       */
      /**
       * Top of whatever is pinned to the bottom of the screen (sticky buy bar, mobile nav,
       * chat launcher strip ...): any visible fixed/sticky element sitting in the lower half,
       * so the list never opens behind one, whichever bar a template uses.
       */
      bottomBarTop() {
        let top = window.innerHeight;
        const candidates = document.querySelectorAll(
          '.main-add-to-basket, .mobile-sticky-nav, [class*="sticky-atc"], [class*="sticky-add"], [class*="sticky-bar"], [class*="sticky-cart"]'
        );
        candidates.forEach((el) => {
          if (el.contains(this) || el.offsetParent === null && getComputedStyle(el).position !== 'fixed') return;
          const style = getComputedStyle(el);
          if (style.display === 'none' || style.visibility === 'hidden') return;
          if (style.position !== 'fixed' && style.position !== 'sticky') return;
          const rect = el.getBoundingClientRect();
          if (rect.height > 0 && rect.top > window.innerHeight / 2 && rect.top < window.innerHeight) {
            top = Math.min(top, rect.top);
          }
        });
        return top;
      }

      /**
       * On open only: if the input sits partly behind a bottom bar (the sticky buy bar), scroll
       * it up into view, so neither the input nor its list is drawn over or under the bar.
       * Not repeated on scroll - that would fight the customer's own scrolling.
       */
      revealAboveBottomBar() {
        const barTop = this.bottomBarTop();
        const buttonRect = this.button.getBoundingClientRect();
        if (buttonRect.bottom > barTop - 8) {
          window.scrollBy({ top: buttonRect.bottom - barTop + 16, behavior: 'auto' });
        }
      }

      placeList() {
        this.classList.remove('fd--up');

        const button = this.button.getBoundingClientRect();
        const listHeight = this.list.offsetHeight;
        // The lowest point the list may reach: the screen, the sticky buy bar, and - in the cart
        // drawer - the bottom of the scrolling items area, below which the drawer's own footer
        // (recommendations, promo code, total) sits.
        let bottomEdge = Math.min(window.innerHeight, this.bottomBarTop());
        const scroller = this.closest('.drawer__contents, .drawer__cart-items-wrapper, cart-items');
        if (scroller) bottomEdge = Math.min(bottomEdge, scroller.getBoundingClientRect().bottom);
        const footer = this.closest('cart-drawer')?.querySelector('.drawer__footer');
        if (footer) bottomEdge = Math.min(bottomEdge, footer.getBoundingClientRect().top);
        const spaceBelow = bottomEdge - button.bottom - 8;
        const spaceAbove = button.top - 8;
        // Above when the input is in the lower part of the visible area (or the list simply does
        // not fit below) and there is room above; below when it is at the top or middle.
        const visibleHeight = bottomEdge;
        const inLowerPart = button.top > visibleHeight * 0.6;
        const fitsAbove = listHeight <= spaceAbove;
        if ((inLowerPart || listHeight > spaceBelow) && (fitsAbove || spaceAbove > spaceBelow)) {
          this.classList.add('fd--up');
        }
      }

      onButtonClick(event) {
        // The product card is a <label>; without this the click would also toggle
        // the card's radio.
        event.preventDefault();
        event.stopPropagation();
        this.setOpen(!this.open);
      }

      onDocumentClick(event) {
        if (!this.open) return;
        if (!this.contains(event.target)) this.setOpen(false);
      }

      /* ── Selection ─────────────────────────────────────────────────────── */

      currentOption() {
        return (
          this.options.find((o) => o.dataset.plan === String(this.dataset.selected)) ||
          this.options[0]
        );
      }

      syncFromSelected() {
        const current = this.currentOption();
        this.options.forEach((o) => {
          const on = o === current;
          o.setAttribute('aria-selected', on ? 'true' : 'false');
          o.classList.toggle('is-selected', on);
        });
        if (current && this.valueEl) {
          this.valueEl.textContent = current.dataset.label || current.textContent.trim();
        }
      }

      /** Set from outside without re-notifying, so the card can drive the default. */
      setSelected(planId) {
        if (!planId || String(planId) === String(this.dataset.selected)) return;
        this.dataset.selected = String(planId);
        this.syncFromSelected();
      }

      onOptionClick(event) {
        const option = event.target.closest('.fd__option');
        if (!option) return;
        event.preventDefault();
        event.stopPropagation();
        this.choose(option);
      }

      choose(option) {
        const planId = option.dataset.plan;
        this.dataset.selected = String(planId);
        this.syncFromSelected();
        this.setOpen(false);
        this.button.focus();

        this.dispatchEvent(
          new CustomEvent('freq:change', {
            bubbles: true,
            detail: {
              planId,
              months: parseInt(option.dataset.months || '0', 10),
              label: option.dataset.label || option.textContent.trim(),
            },
          })
        );

        if (this.lineKey) this.changeCartLine(planId);
      }

      /* ── Keyboard ──────────────────────────────────────────────────────── */

      onKeydown(event) {
        const { key } = event;

        if (!this.open) {
          if (key === 'ArrowDown' || key === 'Enter' || key === ' ') {
            event.preventDefault();
            this.setOpen(true);
          }
          return;
        }

        if (key === 'Escape' || key === 'Tab') {
          this.setOpen(false);
          if (key === 'Escape') this.button.focus();
          return;
        }

        const focused = document.activeElement.closest?.('.fd__option');
        const index = this.options.indexOf(focused);

        if (key === 'ArrowDown' || key === 'ArrowUp') {
          event.preventDefault();
          const next = key === 'ArrowDown' ? index + 1 : index - 1;
          const clamped = (next + this.options.length) % this.options.length;
          this.options[clamped].focus();
        } else if (key === 'Enter' || key === ' ') {
          event.preventDefault();
          if (focused) this.choose(focused);
        }
      }

      /* ── Cart ──────────────────────────────────────────────────────────── */

      /**
       * Swap the selling plan on this cart line. change.js keeps the line in place
       * rather than removing and re-adding, so quantity and position survive.
       *
       * Addressed by the line item key rather than by `line`. A line number is only
       * valid for the cart as it stood when the HTML was rendered, and this store's
       * cart script adds and removes free gift lines on its own schedule — every line
       * after one of those is renumbered, so a stale number would change a different
       * product's plan. The theme's own gift add and remove use the key for the same
       * reason.
       */
      /**
       * The line's current quantity. change.js treats a request without a quantity as
       * quantity 0, which REMOVES the line - so the interval swap must always resend it.
       * Read from the cart itself (by key) so a stale stepper value can never be used;
       * the line's own stepper is only the fallback.
       */
      async currentLine() {
        try {
          const cart = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
            headers: { Accept: 'application/json' },
          }).then((res) => res.json());
          const line = (cart.items || []).find((item) => item.key === this.lineKey);
          if (line) return { quantity: line.quantity, variantId: line.variant_id };
        } catch (error) {
          // fall through to the stepper
        }
        const input = this.closest('.cart-item')?.querySelector('.quantity__input');
        const stepped = parseInt(input?.value, 10);
        return {
          quantity: stepped > 0 ? stepped : 1,
          variantId: Number(input?.dataset.quantityVariantId) || null,
        };
      }

      async changeCartLine(planId) {
        this.classList.add('is-busy');
        try {
          const { quantity, variantId } = await this.currentLine();
          const response = await fetch(`${window.routes?.cart_change_url || '/cart/change'}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: this.lineKey, quantity, selling_plan: Number(planId) }),
          });

          if (!response.ok) throw new Error(`cart/change returned ${response.status}`);

          // Safety net: if the line is no longer in the cart after the swap, put it straight
          // back on the chosen plan, so changing the interval can never empty the basket.
          const updated = await response.json().catch(() => null);
          const stillThere = (updated?.items || []).some((item) => item.variant_id === variantId);
          if (variantId && updated && !stillThere) {
            await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: [{ id: variantId, quantity, selling_plan: Number(planId) }] }),
            });
          }

          // Reuse the theme's own refreshers so the drawer and the cart page update
          // the same way they do for any other cart edit.
          if (document.querySelector('cart-drawer')) await window.refreshCartDrawer?.();
          if (document.querySelector('cart-items')) await window.refreshCart?.();
        } catch (error) {
          console.warn('freq-dropdown: could not change the delivery interval', error);
          this.classList.remove('is-busy');
        }
      }
    }
  );
}
